import type { MeetingCubit } from '@breakoutlearning/firebase-repository/cubits/MeetingCubit'
import { RoomContext } from '@livekit/components-react'
import { usePersistentUserChoicesWithOutput } from 'hooks/usePersistentUserChoicesWithOutput'
import type { RoomOptions } from 'livekit-client'
import { Room, VideoPresets } from 'livekit-client'
import { useEffect, useMemo } from 'react'

const roomOptions: RoomOptions = {
  dynacast: true,
  adaptiveStream: { pixelDensity: 'screen' },
  videoCaptureDefaults: {
    facingMode: 'user',
    resolution: VideoPresets.h720,
  },
  publishDefaults: {
    videoCodec: 'vp9',
    simulcast: true,
    videoSimulcastLayers: [
      VideoPresets.h180,
      VideoPresets.h360,
      VideoPresets.h720,
      VideoPresets.h1080,
    ],
  },
  audioCaptureDefaults: {
    echoCancellation: true,
    autoGainControl: true,
  },
}

/**
 * Owns the single LiveKit Room instance for a meeting and exposes it via
 * `RoomContext` so any LiveKit hook can read it.
 *
 * Phase 1 plumbing only: the Room is constructed here but is not connected
 * until `<LiveKitRoom>` inside MeetingPage mounts and calls connect. AvCheck
 * still uses its own Room via `MeetingDeviceConfigurationController` for now.
 */
export function SharedLiveKitProvider({
  meeting,
  children,
}: {
  meeting: MeetingCubit
  children: React.ReactNode
}) {
  const { userChoices } = usePersistentUserChoicesWithOutput()

  const room = useMemo(() => {
    const optionsWithUserChoices: RoomOptions = {
      ...roomOptions,
      videoCaptureDefaults: {
        ...roomOptions.videoCaptureDefaults,
        deviceId: userChoices.videoDeviceId,
      },
      audioCaptureDefaults: {
        ...roomOptions.audioCaptureDefaults,
        deviceId: userChoices.audioDeviceId,
      },
    }
    const newRoom = new Room(optionsWithUserChoices)
    meeting.setLivekitRoom(newRoom)
    return newRoom
    // userChoices intentionally read only at construction time, matching the
    // previous behaviour in LiveKitRoomWrapper. Device changes during the
    // meeting go through `room.switchActiveDevice`, not Room recreation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting])

  useEffect(() => {
    const roomToDisconnect = room
    return () => {
      roomToDisconnect.disconnect(true)
    }
  }, [room])

  return <RoomContext.Provider value={room}>{children}</RoomContext.Provider>
}
